/**
 * NOS-128: Public media listing at /media or /:orgSlug/media.
 * NOS-265: org/nav/customizations shell via usePublicSiteShell + PublicSitePage.
 */
import { useMemo, useRef } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { Podcast, Video } from "lucide-react"
import { PublicSitePage } from "@/components/PublicSitePage"
import { ListingPagination, useScrollToListingTop } from "@/components/public/ListingPagination"
import { SEOHead } from "@/components/public/SEOHead"
import { getPublicSiteOrigin } from "@/lib/publicSiteUrl"
import { usePublicSiteShell } from "@/hooks/usePublicSiteShell"
import { usePublicMedia, usePublicMediaTaxonomies } from "@/hooks/usePublicMedia"
import { getPodcastFeedUrl } from "@/lib/podcastFeedUrl"
import type { Organization, OrgCustomizations } from "@/templates/types"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

interface MediaListingPageProps {
  orgSlug: string
  org?: Organization | null
  customizations?: OrgCustomizations | null
  basePath?: string
}

function isPodcastConfigured(raw: unknown): boolean {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return false
  return Object.keys(raw as object).length > 0
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })
}

export function MediaListingPage({
  orgSlug,
  org: orgProp,
  customizations: custProp,
  basePath = "",
}: MediaListingPageProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const seriesSlug = searchParams.get("series")
  const speakerIdRaw = searchParams.get("speaker")
  const speakerId =
    speakerIdRaw && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(speakerIdRaw)
      ? speakerIdRaw
      : null
  const topicSlugs = searchParams.getAll("topic").filter(Boolean)
  const pageParam = parseInt(searchParams.get("page") ?? "1", 10)
  const page = Math.max(1, Number.isNaN(pageParam) ? 1 : pageParam)

  const shell = usePublicSiteShell(orgSlug, {
    org: orgProp,
    customizations: custProp,
    basePath,
  })
  const { org, orgName, primaryColor, loading: loadingOrg } = shell

  const orgId = org?.id ?? null

  const filters = useMemo(
    () => ({
      seriesSlug,
      speakerId,
      topicSlugs,
      page,
    }),
    [seriesSlug, speakerId, topicSlugs, page]
  )

  const { videos, totalCount, loading, error, pageSize } = usePublicMedia(orgId, filters)
  const { series, speakers, topics } = usePublicMediaTaxonomies(orgId)

  const pathForMedia = `${basePath}/media`
  const buildMediaListUrl = (overrides: {
    series?: string | null
    speaker?: string | null
    topics?: string[] | null
    page?: number | null
  }) => {
    const p = new URLSearchParams()
    const s = overrides.series !== undefined ? overrides.series : seriesSlug
    const sp = overrides.speaker !== undefined ? overrides.speaker : speakerId
    const tops = overrides.topics !== undefined ? overrides.topics : topicSlugs
    const pg = overrides.page !== undefined ? overrides.page : page
    if (s) p.set("series", s)
    if (sp) p.set("speaker", sp)
    for (const t of tops ?? []) {
      if (t) p.append("topic", t)
    }
    if (pg != null && pg > 1) p.set("page", String(pg))
    const q = p.toString()
    return q ? `${pathForMedia}?${q}` : pathForMedia
  }

  /** NOS-240: scroll the listing (not the whole window) back to its top on page change. */
  const listingTopRef = useRef<HTMLElement>(null)
  useScrollToListingTop(page, listingTopRef)

  const podcastUrl =
    org?.slug && isPodcastConfigured((org as { podcast_settings?: unknown }).podcast_settings)
      ? getPodcastFeedUrl(org.slug)
      : ""

  const canonicalUrl = useMemo(() => {
    if (!org) return ""
    const origin = getPublicSiteOrigin(org)
    const mediaPath = basePath ? `${basePath}/media` : "/media"
    const p = new URLSearchParams()
    if (seriesSlug) p.set("series", seriesSlug)
    if (speakerId) p.set("speaker", speakerId)
    topicSlugs.forEach((t) => p.append("topic", t))
    if (page > 1) p.set("page", String(page))
    const q = p.toString()
    return `${origin}${mediaPath}${q ? `?${q}` : ""}`
  }, [org, basePath, seriesSlug, speakerId, topicSlugs, page])

  const listJsonLd = useMemo(() => {
    if (!org) return null
    const origin = getPublicSiteOrigin(org)
    const baseMedia = basePath ? `${origin}${basePath}/media` : `${origin}/media`
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${orgName} — Media`,
      itemListElement: videos.map((v, i) => ({
        "@type": "ListItem",
        position: i + 1 + (page - 1) * pageSize,
        url: `${baseMedia}/${v.slug}`,
        name: v.title,
      })),
    }
  }, [org, orgName, videos, page, pageSize, basePath])

  const thumbUrl = (youtubeId: string | null, thumb: string | null) =>
    thumb || (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : "")

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const description = `Watch and listen to messages from ${orgName}`
  const showContent = org && !loadingOrg

  return (
    <PublicSitePage
      shell={shell}
      head={
        showContent && canonicalUrl ? (
          <SEOHead
            title={`${orgName} — Media`}
            description={description}
            canonicalUrl={canonicalUrl}
            ogType="website"
            imageUrl={org.logo_url}
            jsonLd={listJsonLd}
          />
        ) : null
      }
    >
      <main ref={listingTopRef} className="mx-auto max-w-6xl scroll-mt-20 px-4 py-12 sm:px-6 lg:px-8">
        <header className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: primaryColor }}>
              Media
            </h1>
            <p className="mt-1 text-muted-foreground">{description}</p>
          </div>
          {podcastUrl ? (
            <Button variant="outline" size="sm" asChild className="shrink-0">
              <a href={podcastUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                <Podcast className="h-4 w-4" />
                Podcast (RSS)
              </a>
            </Button>
          ) : null}
        </header>

        {(series.length > 0 || speakers.length > 0 || topics.length > 0) && (
          <div className="mb-8 space-y-4">
            {series.length > 0 ? (
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Series</p>
                <div className="flex flex-wrap gap-2">
                  <Link to={buildMediaListUrl({ series: null, page: 1 })}>
                    <Badge variant={!seriesSlug ? "default" : "outline"} className="cursor-pointer">
                      All
                    </Badge>
                  </Link>
                  {series.map((s) => (
                    <Link key={s.id} to={buildMediaListUrl({ series: s.slug, page: 1 })}>
                      <Badge
                        variant={seriesSlug === s.slug ? "default" : "outline"}
                        className="cursor-pointer"
                      >
                        {s.title}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}
            {speakers.length > 0 ? (
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Speakers</p>
                <div className="flex flex-wrap gap-2">
                  <Link to={buildMediaListUrl({ speaker: null, page: 1 })}>
                    <Badge variant={!speakerId ? "default" : "outline"} className="cursor-pointer">
                      All
                    </Badge>
                  </Link>
                  {speakers.map((s) => (
                    <Link key={s.id} to={buildMediaListUrl({ speaker: s.id, page: 1 })}>
                      <Badge variant={speakerId === s.id ? "default" : "outline"} className="cursor-pointer">
                        {s.name}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}
            {topics.length > 0 ? (
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Topics</p>
                <div className="flex flex-wrap gap-2">
                  <Link to={buildMediaListUrl({ topics: [], page: 1 })}>
                    <Badge variant={topicSlugs.length === 0 ? "default" : "outline"} className="cursor-pointer">
                      All
                    </Badge>
                  </Link>
                  {topics.map((t) => {
                    const on = topicSlugs.includes(t.slug)
                    const nextTopics = on ? topicSlugs.filter((x) => x !== t.slug) : [...topicSlugs, t.slug]
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                          const p = new URLSearchParams()
                          if (seriesSlug) p.set("series", seriesSlug)
                          if (speakerId) p.set("speaker", speakerId)
                          for (const slug of nextTopics) p.append("topic", slug)
                          setSearchParams(p)
                        }}
                      >
                        <Badge variant={on ? "default" : "outline"} className="cursor-pointer">
                          {t.name}
                        </Badge>
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : null}
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
        )}

        {loading || loadingOrg ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="aspect-video bg-muted" />
                <CardContent className="p-4">
                  <div className="h-4 w-3/4 rounded bg-muted" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : videos.length === 0 ? (
          <div className="rounded-lg border border-dashed py-16 text-center">
            <Video className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
            <p className="text-muted-foreground">No published messages match these filters yet.</p>
            {(seriesSlug || speakerId || topicSlugs.length > 0) && (
              <Button variant="outline" className="mt-4" onClick={() => setSearchParams(new URLSearchParams())}>
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {videos.map((v) => (
                <Link key={v.id} to={`${basePath}/media/${v.slug}`} className="group block">
                  <Card className="h-full overflow-hidden transition-shadow group-hover:shadow-md">
                    <div className="aspect-video overflow-hidden bg-muted">
                      {thumbUrl(v.youtube_video_id, v.thumbnail_url) ? (
                        <img
                          src={thumbUrl(v.youtube_video_id, v.thumbnail_url)}
                          alt=""
                          className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
                        />
                      ) : null}
                    </div>
                    <CardContent className="p-4">
                      <h2 className="line-clamp-2 font-semibold leading-snug group-hover:underline">{v.title}</h2>
                      <p className="mt-1 text-sm text-muted-foreground">{formatDate(v.published_at)}</p>
                      {v.media_series?.title ? (
                        <p className="mt-2 text-xs text-muted-foreground">Series: {v.media_series.title}</p>
                      ) : null}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            <ListingPagination
              page={page}
              totalPages={totalPages}
              size="sm"
              getPageHref={(pg) => buildMediaListUrl({ page: pg })}
              onPageChange={(pg) => navigate(buildMediaListUrl({ page: pg }))}
            />
          </>
        )}
      </main>
    </PublicSitePage>
  )
}
